import React, { useState, useRef, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import ExcelJS from 'exceljs';

// Column definitions for CCR
const CCR_COLUMNS = [
  { key: 'change_request_number_lgl', label: 'CR# LGL' },
  { key: 'change_type_fwo_ccr', label: 'Type (FWO/CCR)' },
  { key: 'ccr_or_fwo_id', label: 'CCR/FWO ID' },
  { key: 'extra_number', label: 'Extra #' },
  { key: 'change_order_number_client', label: 'Client CO#' },
  { key: 'subject', label: 'Subject' },
  { key: 'area', label: 'Area' },
  { key: 'sub_area', label: 'Sub Area' },
  { key: 'discipline', label: 'Discipline' },
  { key: 'contract_type_costplus_timeandmaterial_lumpsum_unitprice', label: 'Contract Type' },
  { key: 'impact_schedule', label: 'Impact Schedule' },
  { key: 'impact_cost', label: 'Impact Cost CAD' },
  { key: 'budget_hour_estimated', label: 'Budget Hours' },
  { key: 'budget_cost_labour', label: 'Budget Labour CAD' },
  { key: 'budget_cost_non_labour', label: 'Budget Non-Labour CAD' },
  { key: 'budget_cost_total', label: 'Budget Total CAD' },
  { key: 'date_issued_ccr', label: 'Date Issued' },
  { key: 'date_signed_by_client_ccr', label: 'Date Signed' },
  { key: 'date_cco', label: 'Date CCO' },
  { key: 'treatment_time', label: 'Treatment Time' },
  { key: 'tracking_method', label: 'Tracking Method' },
  { key: 'progress_percentage', label: 'Progress %' },
  { key: 'actual_spent_hour', label: 'Actual Hours' },
  { key: 'earned_hrs', label: 'Earned Hours' },
  { key: 'variance_earned_to_spent', label: 'Variance' },
  { key: 'cpi_per_ccr', label: 'CPI' },
  { key: 'reference_document_or_hyper_links', label: 'Reference/Links' },
  { key: 'comments', label: 'Comments' },
  { key: 'responsible', label: 'Responsible' },
];

// Add UDF columns
for (let i = 1; i <= 16; i++) {
  CCR_COLUMNS.push({
    key: `udf${String(i).padStart(2, '0')}`,
    label: `UDF${String(i).padStart(2, '0')}`,
  });
}

const CCRUploadModal = React.memo(({ isOpen, onClose, onUploadSuccess, onUploadError, jobNumber }) => {
  const { instance, accounts } = useMsal();
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [parsedData, setParsedData] = useState(null);
  const [columnMatches, setColumnMatches] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [step, setStep] = useState('select'); // 'select', 'validate', 'confirm', 'uploading'
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        setSelectedFile(file);
        parseExcelFile(file);
      } else {
        onUploadError?.('Please select an Excel file (.xlsx or .xls)');
      }
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        setSelectedFile(file);
        parseExcelFile(file);
      } else {
        onUploadError?.('Please select an Excel file (.xlsx or .xls)');
      }
    }
  };

  const parseExcelFile = useCallback(async (file) => {
    setStep('validate');
    setValidationError('');
    
    try {
      const workbook = new ExcelJS.Workbook();
      const arrayBuffer = await file.arrayBuffer();
      await workbook.xlsx.load(arrayBuffer);
      
      const worksheet = workbook.worksheets[0];
      if (!worksheet) {
        throw new Error('No worksheet found in the Excel file');
      }
      
      // Get headers from first row
      const headerRow = worksheet.getRow(1);
      const headers = [];
      headerRow.eachCell((cell, colNumber) => {
        headers.push({
          colNumber,
          value: String(cell.value || '').trim(),
        });
      });
      
      // Match headers to CCR columns
      const matches = {};
      const unmatchedHeaders = [];
      
      headers.forEach(header => {
        const normalizedHeader = header.value.toLowerCase().replace(/[^a-z0-9]/g, '');
        
        // Try to find matching column
        const match = CCR_COLUMNS.find(col => {
          const normalizedKey = col.key.toLowerCase().replace(/[^a-z0-9]/g, '');
          const normalizedLabel = col.label.toLowerCase().replace(/[^a-z0-9]/g, '');
          return normalizedHeader === normalizedKey || normalizedHeader === normalizedLabel;
        });
        
        if (match) {
          matches[header.colNumber] = match.key;
        } else if (header.value) {
          unmatchedHeaders.push(header.value);
        }
      });
      
      setColumnMatches(matches);
      
      // Parse data rows
      const data = [];
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return; // Skip header row
        
        const rowData = { job_number: jobNumber };
        let hasData = false;
        
        Object.entries(matches).forEach(([colNumber, columnKey]) => {
          const cell = row.getCell(parseInt(colNumber));
          let value = cell.value;
          
          // Handle different cell value types
          if (value && typeof value === 'object') {
            if (value.result !== undefined) value = value.result;
            else if (value.text) value = value.text;
            else if (value instanceof Date) value = value.toISOString().split('T')[0];
          }
          
          if (value !== null && value !== undefined && value !== '') {
            rowData[columnKey] = value;
            hasData = true;
          }
        });
        
        if (hasData) {
          data.push(rowData);
        }
      });
      
      if (data.length === 0) {
        throw new Error('No data rows found in the Excel file');
      }
      
      setParsedData(data);
      setStep('confirm');
      
      if (unmatchedHeaders.length > 0) {
        console.log('Unmatched columns:', unmatchedHeaders);
      }
    } catch (error) {
      console.error('Error parsing Excel file:', error);
      setValidationError(error.message || 'Failed to parse Excel file');
      setStep('select');
    }
  }, [jobNumber]);

  const handleUpload = async () => {
    if (!parsedData || parsedData.length === 0) {
      onUploadError?.('No data to upload');
      return;
    }
    
    setStep('uploading');
    setLoading(true);
    setUploadProgress(0);
    setUploadStatus('Uploading data...');
    
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        '/ccr-tracking/upload-parsed',
        {
          job_number: jobNumber,
          data: parsedData,
        }
      );
      
      if (response.data.success) {
        setUploadProgress(100);
        setUploadStatus('Upload completed successfully!');
        onUploadSuccess?.(response.data);
        
        // Auto-close after success
        setTimeout(() => {
          handleClose();
        }, 1500);
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus('Upload failed');
      onUploadError?.(error.message || 'Upload failed');
      setStep('confirm');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep('select');
    setSelectedFile(null);
    setParsedData(null);
    setColumnMatches(null);
    setValidationError('');
    setUploadProgress(0);
    setUploadStatus('');
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '2rem',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
        }}>
          <h3 style={{ margin: 0, color: '#333' }}>
            📤 Import Change Orders
          </h3>
          <button
            onClick={handleClose}
            disabled={loading}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              color: '#666',
              padding: '0.25rem',
              opacity: loading ? 0.5 : 1,
            }}
          >
            ×
          </button>
        </div>

        {/* Step: Select File */}
        {step === 'select' && (
          <>
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${dragActive ? '#17a2b8' : '#dee2e6'}`,
                borderRadius: '8px',
                padding: '3rem 2rem',
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: dragActive ? '#e3f2fd' : '#f8f9fa',
                transition: 'all 0.2s ease',
              }}
            >
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📁</div>
              <p style={{ margin: 0, color: '#666' }}>
                Drag and drop an Excel file here, or click to browse
              </p>
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem', color: '#999' }}>
                Supported formats: .xlsx, .xls
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            
            {validationError && (
              <div style={{
                marginTop: '1rem',
                padding: '0.75rem',
                backgroundColor: '#f8d7da',
                color: '#721c24',
                borderRadius: '4px',
              }}>
                {validationError}
              </div>
            )}
          </>
        )}

        {/* Step: Validating */}
        {step === 'validate' && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
            <p style={{ color: '#666' }}>Validating Excel file structure...</p>
          </div>
        )}

        {/* Step: Confirm */}
        {step === 'confirm' && parsedData && (
          <>
            <div style={{
              backgroundColor: '#d4edda',
              color: '#155724',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
            }}>
              <strong>✓ File validated successfully!</strong>
              <p style={{ margin: '0.5rem 0 0' }}>
                Found {parsedData.length} rows ready to import.
              </p>
            </div>
            
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
              maxHeight: '200px',
              overflowY: 'auto',
            }}>
              <strong>Matched columns:</strong>
              <div style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
                {columnMatches && Object.values(columnMatches).slice(0, 10).map((col, i) => (
                  <span key={i} style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    backgroundColor: '#e9ecef',
                    borderRadius: '3px',
                    margin: '2px',
                  }}>
                    {col}
                  </span>
                ))}
                {columnMatches && Object.keys(columnMatches).length > 10 && (
                  <span style={{ color: '#666' }}>
                    +{Object.keys(columnMatches).length - 10} more
                  </span>
                )}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setStep('select')}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Back
              </button>
              <button
                onClick={handleUpload}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Import {parsedData.length} Rows
              </button>
            </div>
          </>
        )}

        {/* Step: Uploading */}
        {step === 'uploading' && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
              {uploadProgress === 100 ? '✓' : '⏳'}
            </div>
            <p style={{ color: '#666', marginBottom: '1rem' }}>{uploadStatus}</p>
            <div style={{
              width: '100%',
              height: '8px',
              backgroundColor: '#e9ecef',
              borderRadius: '4px',
              overflow: 'hidden',
            }}>
              <div style={{
                width: `${uploadProgress}%`,
                height: '100%',
                backgroundColor: uploadProgress === 100 ? '#28a745' : '#17a2b8',
                transition: 'width 0.3s ease',
              }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default CCRUploadModal;
